import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  Modal,
  Nav,
  OverlayTrigger,
  Row,
  Spinner,
  Tab,
  Table,
  Tooltip,
} from 'react-bootstrap'
import {
  CampaignChannel,
  ChannelVariant,
  ICampaign,
  ICampaignLog,
  ICampaignTarget,
  IMessageTemplate,
  CampaignTarget,
  CampaignLog,
} from '../../../types/campaigns'
import { ChannelBadge } from './ChannelBadge'
import clsx from 'clsx'
import { toast } from 'react-toastify'
import { VariantPreview } from '../../templates/components/VariantPreview'
import { PhoneProviderActions } from '../../../components/telephony/PhoneProviderActions'

type Props = {
  campaign?: ICampaign | null
  targetsData?: CampaignTarget[]
  logsData?: CampaignLog[]
  loadingTargets?: boolean
  loadingLogs?: boolean
  onEditCampaign: () => void
  onAddTarget: () => void
  onLaunchCampaign: () => void
  onPauseCampaign: () => void
  launching?: boolean
  pausing?: boolean
  onSendTest: (
    recipient: string,
    channels: CampaignChannel[],
    advancedOptions?: {
      fromEmail?: string
      fromName?: string
      replyTo?: string
      includeLogo?: boolean
    }
  ) => Promise<void>
  onEditTemplate: (template: IMessageTemplate) => void
  onRefreshTargets: () => void
  onRefreshLogs: () => void
  onRemoveTarget: (targetId: string) => Promise<void>
  onClearTargets?: () => Promise<void>
  clearingTargets?: boolean
  onStopCampaign?: () => Promise<void>
  stopping?: boolean
  onDelete: () => Promise<void>
  autoRefresh?: boolean
  onToggleAutoRefresh?: (enabled: boolean) => void
}

const formatDate = (value?: string | null) => {
  if (!value) return 'Not set'
  try {
    return new Date(value).toLocaleString()
  } catch {
    return value
  }
}

const statusBadgeVariant: Record<string, string> = {
  DRAFT: 'secondary',
  IN_PROGRESS: 'success',
  SCHEDULED: 'primary',
  PAUSED: 'warning',
  COMPLETED: 'info',
  FAILED: 'danger',
}

const channelOptions: CampaignChannel[] = ['SMS', 'EMAIL', 'WHATSAPP', 'TELEGRAM']

// const Pagination = ({
//   page,
//   totalPages,
//   totalCount,
//   pageSize,
//   loading,
//   onChange,
// }: {
//   page: number
//   totalPages: number
//   totalCount: number
//   pageSize: number
//   loading?: boolean
//   onChange: (page: number) => void
// }) => {
//   if (totalPages <= 1) return null
//   const from = (page - 1) * pageSize + 1
//   const to = Math.min(page * pageSize, totalCount)
//   return (
//     <div className='d-flex align-items-center justify-content-between px-4 py-3 border-top'>
//       <span className='text-muted fs-7'>
//         {loading ? 'Loading…' : `Showing ${from}–${to} of ${totalCount}`}
//       </span>
//       <div className='d-flex gap-1'>
//         <Button
//           size='sm'
//           variant='light'
//           disabled={page <= 1 || loading}
//           onClick={() => onChange(page - 1)}
//         >
//           <i className='bi bi-chevron-left'></i>
//         </Button>
//         {Array.from({ length: totalPages }, (_, i) => i + 1)
//           .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
//           .reduce<(number | '…')[]>((acc, p, idx, arr) => {
//             if (idx > 0 && p - (arr[idx - 1] as number) > 1) acc.push('…')
//             acc.push(p)
//             return acc
//           }, [])
//           .map((p, idx) =>
//             p === '…' ? (
//               <span key={`ellipsis-${idx}`} className='btn btn-sm btn-light disabled'>…</span>
//             ) : (
//               <Button
//                 key={p}
//                 size='sm'
//                 variant={p === page ? 'primary' : 'light'}
//                 onClick={() => onChange(p as number)}
//                 disabled={loading}
//               >
//                 {p}
//               </Button>
//             )
//           )}
//         <Button
//           size='sm'
//           variant='light'
//           disabled={page >= totalPages || loading}
//           onClick={() => onChange(page + 1)}
//         >
//           <i className='bi bi-chevron-right'></i>
//         </Button>
//       </div>
//     </div>
//   )
// }

export const CampaignDetail = ({
  campaign,
  targetsData,
  logsData,
  loadingTargets,
  loadingLogs,
  onEditCampaign,
  onAddTarget,
  onLaunchCampaign,
  onPauseCampaign,
  launching,
  pausing,
  onSendTest,
  onEditTemplate,
  onRefreshTargets,
  onRefreshLogs,
  onRemoveTarget,
  onClearTargets,
  clearingTargets,
  onStopCampaign,
  stopping,
  onDelete,
  autoRefresh = false,
  onToggleAutoRefresh,
}: Props) => {
  const navigate = useNavigate()
  const [testRecipient, setTestRecipient] = useState('')
  const [selectedChannels, setSelectedChannels] = useState<CampaignChannel[]>(['EMAIL'])
  const [sendingTest, setSendingTest] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showStopModal, setShowStopModal] = useState(false)
  const [showClearModal, setShowClearModal] = useState(false)
  const [showDeleteTargetModal, setShowDeleteTargetModal] = useState(false)
  const [selectedTarget, setSelectedTarget] = useState<ICampaignTarget | null>(null)
  const [showAdvancedTests, setShowAdvancedTests] = useState(false)
  const [previewVariant, setPreviewVariant] = useState<ChannelVariant | null>(null)
  const [rightPanelOpen, setRightPanelOpen] = useState(true)
  const [activeTab, setActiveTab] = useState<'targets' | 'logs'>('targets')

  // Advanced test options
  const [testFromEmail, setTestFromEmail] = useState('')
  const [testFromName, setTestFromName] = useState('')
  const [testReplyTo, setTestReplyTo] = useState('')
  const [testIncludeLogo, setTestIncludeLogo] = useState(false)

  const enabledChannels = useMemo(() => {
    const raw = campaign?.enabledChannels as unknown
    if (Array.isArray(raw)) return raw as CampaignChannel[]
    if (typeof raw === 'string') {
      return raw.split(',').map((c) => c.trim()).filter(Boolean) as CampaignChannel[]
    }
    return []
  }, [campaign?.enabledChannels])

  const targets: ICampaignTarget[] = targetsData ?? []
  const logs: ICampaignLog[] = logsData ?? []

  const toggleChannel = (channel: CampaignChannel) => {
    setSelectedChannels((prev) =>
      prev.includes(channel) ? prev.filter((c) => c !== channel) : [...prev, channel]
    )
  }

  const handleSendTest = async () => {
    if (!testRecipient || selectedChannels.length === 0) return
    setSendingTest(true)
    try {
      await onSendTest(testRecipient, selectedChannels, {
        fromEmail: testFromEmail || undefined,
        fromName: testFromName || undefined,
        replyTo: testReplyTo || undefined,
        includeLogo: testIncludeLogo,
      })
      setTestRecipient('')
    } finally {
      setSendingTest(false)
    }
  }

  const handleDeleteTargetClick = (target: ICampaignTarget) => {
    setSelectedTarget(target)
    setShowDeleteTargetModal(true)
  }

  const confirmDeleteTarget = async () => {
    if (!selectedTarget) return
    try {
      await onRemoveTarget(selectedTarget.id)
      toast.success('Target removed successfully')
      setShowDeleteTargetModal(false)
      setSelectedTarget(null)
    } catch {
      toast.error('Error removing target')
    }
  }

  if (!campaign) {
    return (
      <div className='card h-100'>
        <div className='card-body d-flex align-items-center justify-content-center text-muted'>
          {loadingTargets ? <Spinner animation='border' size='sm' /> : 'Campaign not found'}
        </div>
      </div>
    )
  }

  const statusVariant = statusBadgeVariant[campaign.status] ?? 'secondary'

  return (
    <>
      {/* ── Header ── */}
      <div className='card mb-5'>
        <div className='card-body'>
          <div className='d-flex justify-content-between align-items-start flex-wrap gap-4'>
            <div>
              <div className='d-flex align-items-center gap-3 mb-1'>
                <h2 className='mb-0'>{campaign.name}</h2>
                <Badge bg={statusVariant}>{campaign.status}</Badge>
                {autoRefresh && <Spinner animation='grow' variant='primary' size='sm' />}
              </div>
              <div className='text-muted'>{campaign.description || 'No description added'}</div>
            </div>
            <div className='d-flex flex-wrap gap-2 align-items-center'>
              <Form.Check
                type='switch'
                id='auto-refresh-switch'
                label='Auto-refresh'
                checked={autoRefresh}
                onChange={(e) => onToggleAutoRefresh?.(e.target.checked)}
                className='mb-0'
              />
              <Button
                variant='light'
                size='sm'
                onClick={() => { onRefreshTargets(); onRefreshLogs() }}
                title='Refresh data'
              >
                <i className='bi bi-arrow-clockwise'></i>
              </Button>
              {campaign.status === 'DRAFT' && (
                <Button variant='light-danger' size='sm' onClick={() => setShowDeleteModal(true)}>
                  <i className='bi bi-trash'></i>
                </Button>
              )}
              <Button variant='light' size='sm' onClick={onEditCampaign}>Edit campaign</Button>
              <Button variant='secondary' size='sm' onClick={onAddTarget}>Add target</Button>
              {campaign.scheduleType === 'RECURRING' &&
                ['ACTIVE', 'PAUSED', 'IN_PROGRESS'].includes(campaign.status) && (
                  <Button
                    variant='outline-danger'
                    size='sm'
                    onClick={() => setShowStopModal(true)}
                    disabled={stopping}
                  >
                    {stopping && <Spinner animation='border' size='sm' className='me-1' />}
                    <i className='bi bi-stop-circle me-1'></i>
                    Stop recurring
                  </Button>
                )}
              {campaign.status === 'ACTIVE' ? (
                <Button variant='warning' size='sm' onClick={onPauseCampaign} disabled={pausing}>
                  {pausing && <Spinner animation='border' size='sm' className='me-1' />}
                  Pause
                </Button>
              ) : (
                <OverlayTrigger
                  placement='top'
                  overlay={
                    campaign.totalTargets === 0
                      ? <Tooltip id='tooltip-launch'>Add at least one target before launching.</Tooltip>
                      : <></>
                  }
                >
                  <span className='d-inline-block'>
                    <Button
                      variant='success'
                      size='sm'
                      onClick={onLaunchCampaign}
                      disabled={launching || campaign.totalTargets === 0}
                      style={campaign.totalTargets === 0 ? { pointerEvents: 'none' } : {}}
                    >
                      {launching && <Spinner animation='border' size='sm' className='me-1' />}
                      Launch
                    </Button>
                  </span>
                </OverlayTrigger>
              )}
            </div>
          </div>

          {/* Meta row */}
          <div className='mt-3 d-flex flex-wrap align-items-center gap-4 text-gray-700'>
            <span><strong>Schedule:</strong> {campaign.scheduleType}</span>
            <span><strong>Next run:</strong> {formatDate(campaign.scheduledTime)}</span>
            <span><strong>Last run:</strong> {formatDate(campaign.lastRunAt)}</span>
            <div className='d-flex gap-2 ms-2'>
              {enabledChannels.map((channel) => (
                <ChannelBadge key={`${campaign.id}-${channel}`} channel={channel as CampaignChannel} />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── Performance metrics ── */}
      <Row className='g-4 mb-5'>
        {[
          { label: 'Targets', value: campaign.totalTargets, variant: 'primary', icon: 'bi-people' },
          { label: 'Sent', value: campaign.sentCount, variant: 'info', icon: 'bi-send' },
          { label: 'Delivered', value: campaign.deliveredCount, variant: 'success', icon: 'bi-check-circle' },
          { label: 'Failed', value: campaign.failedCount, variant: 'danger', icon: 'bi-x-circle' },
        ].map((stat) => (
          <Col xs={6} lg={3} key={stat.label}>
            <div className={`card border-0 bg-light-${stat.variant}`}>
              <div className='card-body d-flex align-items-center gap-3 py-4'>
                <div className={`btn btn-icon btn-${stat.variant} btn-lg`}>
                  <i className={`bi ${stat.icon} fs-3`}></i>
                </div>
                <div>
                  <div className='fs-2 fw-bold'>{stat.value ?? 0}</div>
                  <div className='text-muted fs-7'>{stat.label}</div>
                </div>
              </div>
            </div>
          </Col>
        ))}
      </Row>

      {/* ── Main content + right panel ── */}
      <div className='d-flex gap-5 align-items-start'>
        {/* Tabs card */}
        <div className='flex-grow-1 min-w-0'>
          <Card>
            <Tab.Container activeKey={activeTab} onSelect={(k) => k && setActiveTab(k as 'targets' | 'logs')}>
              <Card.Header className='d-flex align-items-center justify-content-between pe-4' style={{ borderBottom: '1px solid var(--bs-border-color)' }}>
                <Nav variant='tabs' className='border-0 flex-nowrap' style={{ marginBottom: -1 }}>
                  <Nav.Item>
                    <Nav.Link eventKey='targets' className='d-flex align-items-center gap-2 px-4 py-3 fw-semibold'>
                      <i className='bi bi-people'></i>
                      Targets
                      <Badge bg={activeTab === 'targets' ? 'primary' : 'secondary'} pill>
                        {targets.length ?? campaign.totalTargets ?? 0}
                      </Badge>
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey='logs' className='d-flex align-items-center gap-2 px-4 py-3 fw-semibold'>
                      <i className='bi bi-journal-text'></i>
                      Activity log
                      <Badge bg={activeTab === 'logs' ? 'primary' : 'secondary'} pill>
                        {logs.length ?? 0}
                      </Badge>
                    </Nav.Link>
                  </Nav.Item>
                </Nav>
                <div className='d-flex gap-2'>
                  {activeTab === 'targets' ? (
                    <>
                      <Button size='sm' variant='light' onClick={onRefreshTargets} title='Refresh'>
                        <i className='bi bi-arrow-clockwise'></i>
                      </Button>
                      {onClearTargets && targets.length > 0 && campaign.status === 'DRAFT' && (
                        <Button
                          size='sm'
                          variant='light-danger'
                          onClick={() => setShowClearModal(true)}
                          disabled={clearingTargets}
                          title='Remove all targets'
                        >
                          {clearingTargets
                            ? <Spinner animation='border' size='sm' />
                            : <><i className='bi bi-trash me-1'></i>Clear all</>
                          }
                        </Button>
                      )}
                      <Button size='sm' onClick={onAddTarget}>Add target</Button>
                    </>
                  ) : (
                    <Button size='sm' variant='light' onClick={onRefreshLogs} title='Refresh'>
                      <i className='bi bi-arrow-clockwise'></i>
                    </Button>
                  )}
                </div>
              </Card.Header>
              <Tab.Content>
                {/* Targets tab */}
                <Tab.Pane eventKey='targets'>
                  {loadingTargets ? (
                    <div className='campaigns-placeholder'>
                      <Spinner animation='border' size='sm' className='me-2' /> Loading targets
                    </div>
                  ) : targets.length === 0 ? (
                    <div className='campaigns-placeholder'>No targets added yet</div>
                  ) : (
                    <>
                      <div style={{ opacity: loadingTargets ? 0.5 : 1, transition: 'opacity 0.2s' }}>
                        <Table hover size='sm' className='mb-0 align-middle' style={{ whiteSpace: 'nowrap' }}>
                          <thead className='table-light'>
                            <tr>
                              <th className='ps-4'>Contact</th>
                              <th>Email</th>
                              <th>Phone</th>
                              <th>Status</th>
                              <th>Last channel</th>
                              <th>Errors</th>
                              <th className='text-end pe-4'>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {targets.map((target) => (
                              <tr key={target.id}>
                                <td className='ps-4'>
                                  {`${target.contact.firstName ?? ''} ${target.contact.lastName ?? ''}`.trim() || target.contact.id}
                                </td>
                                <td>{target.contact.email || '—'}</td>
                                <td>
                                  <PhoneProviderActions phone={target.contact.phone} emptyText='—' />
                                </td>
                                <td>
                                  <span
                                    className={clsx(
                                      'badge',
                                      target.state === 'FAILED' ? 'badge-light-danger' :
                                        target.state === 'DELIVERED' ? 'badge-light-success' :
                                          target.state === 'SENT' ? 'badge-light-info' :
                                            'badge-light-primary'
                                    )}
                                  >
                                    {target.state}
                                  </span>
                                </td>
                                <td>
                                  {target.lastChannel ? <ChannelBadge channel={target.lastChannel} /> : '—'}
                                </td>
                                <td>{target.errorCount}</td>
                                <td className='text-end pe-4'>
                                  <Button
                                    size='sm'
                                    variant='light-danger'
                                    className='btn-icon'
                                    onClick={() => handleDeleteTargetClick(target)}
                                  >
                                    <i className='bi bi-trash fs-5'></i>
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    </>
                  )}
                </Tab.Pane>

                {/* Activity log tab */}
                <Tab.Pane eventKey='logs'>
                  {loadingLogs ? (
                    <div className='campaigns-placeholder'>
                      <Spinner animation='border' size='sm' className='me-2' /> Loading logs
                    </div>
                  ) : logs.length === 0 ? (
                    <div className='campaigns-placeholder'>No logs found for this campaign</div>
                  ) : (
                    <>
                      <div style={{ opacity: loadingLogs ? 0.5 : 1, transition: 'opacity 0.2s' }}>
                        <Table hover size='sm' className='mb-0 align-middle' style={{ whiteSpace: 'nowrap' }}>
                          <thead className='table-light'>
                            <tr>
                              <th className='ps-4'>Timestamp</th>
                              <th>Channel</th>
                              <th>Status</th>
                              <th>Recipient</th>
                              <th>Provider ID</th>
                              <th>Error</th>
                            </tr>
                          </thead>
                          <tbody>
                            {logs.map((log) => (
                              <tr key={log.id}>
                                <td className='ps-4'>{formatDate(log.createdAt)}</td>
                                <td><ChannelBadge channel={log.channel} /></td>
                                <td>
                                  <span
                                    className={clsx(
                                      'badge',
                                      log.status === 'FAILED' ? 'badge-light-danger' : 'badge-light-success'
                                    )}
                                  >
                                    {log.status}
                                  </span>
                                </td>
                                <td>
                                  {log.contact
                                    ? `${log.contact.firstName ?? ''} ${log.contact.lastName ?? ''}`.trim() ||
                                    log.contact.email || log.contact.id
                                    : '—'}
                                </td>
                                <td>{log.providerMessageId || '—'}</td>
                                <td>
                                  {log.errorMessage ? (
                                    <OverlayTrigger
                                      overlay={<Tooltip id={`log-error-${log.id}`}>{log.errorMessage}</Tooltip>}
                                    >
                                      <span className='text-danger' style={{ cursor: 'pointer' }}>
                                        <i className='bi bi-exclamation-circle me-1'></i>View
                                      </span>
                                    </OverlayTrigger>
                                  ) : '—'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    </>
                  )}
                </Tab.Pane>
              </Tab.Content>
            </Tab.Container>
          </Card>
        </div>

        {/* ── Right panel (collapsible) ── */}
        <div style={{ width: rightPanelOpen ? 320 : 40, flexShrink: 0, transition: 'width 0.25s' }}>
          <div className='d-flex justify-content-end mb-3'>
            <Button
              size='sm'
              variant='light'
              onClick={() => setRightPanelOpen((v) => !v)}
              title={rightPanelOpen ? 'Collapse panel' : 'Expand panel'}
            >
              <i className={`bi bi-layout-sidebar-reverse${rightPanelOpen ? '' : '-reverse'}`}></i>
            </Button>
          </div>

          {rightPanelOpen && (
            <div className='d-flex flex-column gap-4'>
              {/* Template card */}
              {campaign.template ? (
                <Card>
                  <Card.Header>
                    <Card.Title className='fs-6'>Template</Card.Title>
                    <Button
                      size='sm'
                      variant='light'
                      onClick={() => navigate(`/templates/edit/${campaign.template!.id}`)}
                    >
                      Edit
                    </Button>
                  </Card.Header>
                  <Card.Body>
                    <div className='fw-bold mb-2'>{campaign.template.name}</div>
                    {campaign.template.description && (
                      <div className='text-muted small mb-3'>{campaign.template.description}</div>
                    )}
                    {campaign.template.variants && campaign.template.variants.length > 0 ? (
                      campaign.template.variants.map((variant) => (
                        <div key={variant.id} className='border rounded mb-2 overflow-hidden'>
                          <div className='d-flex align-items-center justify-content-between px-3 py-2 bg-light'>
                            <div className='d-flex align-items-center gap-2'>
                              <ChannelBadge channel={variant.channel} />
                              {variant.subject && <span className='small fw-semibold text-truncate' style={{ maxWidth: 130 }}>{variant.subject}</span>}
                            </div>
                            <Button size='sm' variant='white' className='btn-icon' onClick={() => setPreviewVariant(variant)}>
                              <i className='bi bi-eye'></i>
                            </Button>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className='mb-2'>
                        <ChannelBadge channel={campaign.template.channel} />
                        {campaign.template.subject && (
                          <div className='mt-2 small'><strong>Subject:</strong> {campaign.template.subject}</div>
                        )}
                      </div>
                    )}
                  </Card.Body>
                </Card>
              ) : (
                <Alert variant='light'>
                  No template assigned. Add one before launching.
                </Alert>
              )}

              {/* Send test card */}
              <Card>
                <Card.Header>
                  <Card.Title className='fs-6'>Send test message</Card.Title>
                </Card.Header>
                <Card.Body>
                  <Form.Group className='mb-3'>
                    <Form.Label className='small'>Recipient</Form.Label>
                    <Form.Control
                      type='text'
                      size='sm'
                      value={testRecipient}
                      onChange={(e) => setTestRecipient(e.target.value)}
                      placeholder='Email or phone…'
                    />
                  </Form.Group>
                  <div className='mb-3'>
                    <div className='small fw-semibold mb-2'>Channels</div>
                    <div className='d-flex flex-wrap gap-3'>
                      {channelOptions.map((channel) => (
                        <Form.Check
                          key={channel}
                          type='checkbox'
                          label={channel}
                          checked={selectedChannels.includes(channel)}
                          onChange={() => toggleChannel(channel)}
                        />
                      ))}
                    </div>
                  </div>
                  <div className='mb-3'>
                    <Button
                      variant='link'
                      size='sm'
                      className='p-0 text-primary text-decoration-none'
                      onClick={() => setShowAdvancedTests((v) => !v)}
                    >
                      <i className={`bi bi-chevron-${showAdvancedTests ? 'up' : 'down'} me-1`}></i>
                      Advanced Options
                    </Button>
                    {showAdvancedTests && (
                      <div className='mt-2 p-3 bg-light rounded border'>
                        <Form.Group className='mb-2'>
                          <Form.Label className='small fw-bold'>From Name</Form.Label>
                          <Form.Control size='sm' type='text' placeholder='Marketing Team' value={testFromName} onChange={(e) => setTestFromName(e.target.value)} />
                        </Form.Group>
                        <Form.Group className='mb-2'>
                          <Form.Label className='small fw-bold'>From Email</Form.Label>
                          <Form.Control size='sm' type='email' placeholder='news@company.com' value={testFromEmail} onChange={(e) => setTestFromEmail(e.target.value)} />
                        </Form.Group>
                        <Form.Group className='mb-2'>
                          <Form.Label className='small fw-bold'>Reply-To</Form.Label>
                          <Form.Control size='sm' type='email' placeholder='support@company.com' value={testReplyTo} onChange={(e) => setTestReplyTo(e.target.value)} />
                        </Form.Group>
                        <Form.Check type='checkbox' label='Include Company Logo' className='small fw-bold' checked={testIncludeLogo} onChange={(e) => setTestIncludeLogo(e.target.checked)} />
                      </div>
                    )}
                  </div>
                  <Button
                    variant='primary'
                    size='sm'
                    className='w-100'
                    disabled={!testRecipient || selectedChannels.length === 0 || sendingTest}
                    onClick={handleSendTest}
                  >
                    {sendingTest && <Spinner animation='border' size='sm' className='me-1' />}
                    Send test
                  </Button>
                </Card.Body>
              </Card>
            </div>
          )}
        </div>
      </div>

      {/* Preview Modal */}
      <Modal
        show={!!previewVariant}
        onHide={() => setPreviewVariant(null)}
        size={previewVariant?.channel === 'EMAIL' ? 'xl' : undefined}
        centered
        scrollable
      >
        <Modal.Header closeButton>
          <Modal.Title>Preview — {previewVariant?.channel}</Modal.Title>
        </Modal.Header>
        <Modal.Body className='bg-light p-0'>
          <div className='d-flex justify-content-center py-4'>
            {previewVariant && (
              <VariantPreview
                variant={previewVariant}
                variables={{ firstName: 'John', lastName: 'Doe', email: 'john@example.com', phone: '+1234567890' }}
              />
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={() => setPreviewVariant(null)}>Close</Button>
        </Modal.Footer>
      </Modal>

      {/* Stop Recurring Campaign Modal */}
      <Modal show={showStopModal} onHide={() => setShowStopModal(false)} centered>
        <Modal.Header closeButton className='border-0 pb-0'>
          <Modal.Title className='text-danger'>Stop Recurring Campaign</Modal.Title>
        </Modal.Header>
        <Modal.Body className='pt-4 pb-4 text-center'>
          <div className='btn btn-icon btn-light-danger btn-circle btn-lg mb-3'>
            <i className='bi bi-stop-circle fs-1'></i>
          </div>
          <h5>Stop future runs?</h5>
          <div className='text-muted'>
            This will mark <span className='fw-bold'>"{campaign?.name}"</span> as finished.
            No more automated runs will be scheduled.
          </div>
        </Modal.Body>
        <Modal.Footer className='border-0 justify-content-center pb-4'>
          <Button variant='light' onClick={() => setShowStopModal(false)}>Cancel</Button>
          <Button
            variant='danger'
            disabled={stopping}
            onClick={async () => {
              await onStopCampaign?.()
              setShowStopModal(false)
            }}
          >
            {stopping && <Spinner animation='border' size='sm' className='me-1' />}
            Stop recurring
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Campaign Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton className='border-0 pb-0'>
          <Modal.Title className='text-danger'>Delete Campaign</Modal.Title>
        </Modal.Header>
        <Modal.Body className='pt-4 pb-4 text-center'>
          <div className='btn btn-icon btn-light-danger btn-circle btn-lg mb-3'>
            <i className='bi bi-trash fs-1'></i>
          </div>
          <h5>Are you sure?</h5>
          <div className='text-muted'>
            You are about to delete <span className='fw-bold'>"{campaign?.name}"</span>. This cannot be undone.
          </div>
        </Modal.Body>
        <Modal.Footer className='border-0 justify-content-center pb-4'>
          <Button variant='light' onClick={() => setShowDeleteModal(false)}>Cancel</Button>
          <Button variant='danger' onClick={async () => { await onDelete(); setShowDeleteModal(false) }}>
            Delete Campaign
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Clear Targets Confirmation Modal */}
      <Modal show={showClearModal} onHide={() => setShowClearModal(false)} centered>
        <Modal.Header closeButton className='border-0 pb-0'>
          <Modal.Title className='text-danger'>Clear All Targets</Modal.Title>
        </Modal.Header>
        <Modal.Body className='pt-4 pb-4 text-center'>
          <div className='btn btn-icon btn-light-danger btn-circle btn-lg mb-3'>
            <i className='bi bi-trash fs-1'></i>
          </div>
          <h5>Remove all targets?</h5>
          <div className='text-muted'>
            This will remove all{' '}
            <span className='fw-bold'>{targets.length ?? 0}</span> targets from this
            campaign. This cannot be undone.
          </div>
        </Modal.Body>
        <Modal.Footer className='border-0 justify-content-center pb-4'>
          <Button variant='light' onClick={() => setShowClearModal(false)}>
            Cancel
          </Button>
          <Button
            variant='danger'
            disabled={clearingTargets}
            onClick={async () => {
              await onClearTargets?.()
              setShowClearModal(false)
            }}
          >
            {clearingTargets && <Spinner animation='border' size='sm' className='me-1' />}
            Clear all targets
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Target Modal */}
      <Modal show={showDeleteTargetModal} onHide={() => setShowDeleteTargetModal(false)} centered>
        <Modal.Header closeButton className='border-0 pb-0'>
          <Modal.Title>Remove Target</Modal.Title>
        </Modal.Header>
        <Modal.Body className='pt-3 pb-4'>
          <div className='alert alert-warning d-flex align-items-start' role='alert'>
            <i className='bi bi-exclamation-circle me-2 fs-4'></i>
            <div>
              Remove{' '}
              <strong>
                {selectedTarget?.contact.firstName || selectedTarget?.contact.lastName
                  ? `${selectedTarget?.contact.firstName ?? ''} ${selectedTarget?.contact.lastName ?? ''}`.trim()
                  : selectedTarget?.contact.email || selectedTarget?.contact.id}
              </strong>
              ? This cannot be undone.
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className='border-0 justify-content-end'>
          <Button variant='light' onClick={() => setShowDeleteTargetModal(false)}>Cancel</Button>
          <Button variant='danger' onClick={confirmDeleteTarget}>Remove</Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}
